import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { Lead } from "@/types";
import { Users } from "lucide-react";
import { LeadCard } from "@/components/leads/LeadCard";

// Archived/Completed statuses
const COMPLETED_STATUSES = ['partial_disbursed', 'disbursed', 'rejected', 'payout_released', 'closed', 'removed', 'followup_later'];

interface AssociatedLeadsProps {
  entityType: 'connector' | 'banker';
  entityId: string;
}

export function AssociatedLeads({ entityType, entityId }: AssociatedLeadsProps) {
  const { leads } = useLeads();
  const { tasks } = useTasks();

  // Filter leads based on entity type
  const associatedLeads = leads.filter(lead => {
    if (entityType === 'connector') return lead.connectorId === entityId;
    if (entityType === 'banker') return lead.smContactId === entityId;
    return false;
  });

  // Compute pending reminders per lead
  const getPendingReminders = (leadId: string) => {
    return tasks.filter(t => t.leadId === leadId && !t.completed).length;
  };

  // Split into pending (current) and completed (archived)
  const pendingLeads = associatedLeads.filter(l => !COMPLETED_STATUSES.includes(l.status));
  const completedLeads = associatedLeads.filter(l => COMPLETED_STATUSES.includes(l.status));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Associated Leads
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="pending">
              Pending ({pendingLeads.length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({completedLeads.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending" className="mt-4 space-y-3">
            {pendingLeads.length > 0 ? (
              pendingLeads.map(lead => (
                <LeadCard key={lead.id} lead={lead} pendingReminders={getPendingReminders(lead.id)} />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No pending leads for this {entityType === 'connector' ? 'channel partner' : 'banker'}
              </p>
            )}
          </TabsContent>
          
          <TabsContent value="completed" className="mt-4 space-y-3">
            {completedLeads.length > 0 ? (
              completedLeads.map(lead => (
                <LeadCard key={lead.id} lead={lead} pendingReminders={getPendingReminders(lead.id)} />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No completed leads for this {entityType === 'connector' ? 'channel partner' : 'banker'}
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
